import React, { Component } from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    FormControlLabel,
    Checkbox,
    TextField,
    MenuItem,
    Input,
    InputLabel,
    FormControl,
    Select,
    DialogTitle,
    IconButton,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    ListItemText,
    FormLabel
} from '@material-ui/core/';

import InfoIcon from '@material-ui/icons/Info';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDropDown';

import { v4 as uuidv4 } from 'uuid';

import SelectDataType from '@apricityhealth/web-common-lib/components/SelectDataType'
import EditConditions from "@apricityhealth/web-common-lib/components/EditConditions";

import EditModelTypeArrayView from '../components/EditModelTypeArrayView'
import EditDataTypeArray from '../components/EditDataTypeArray'
import EditFlagArrayView from '../components/EditFlagArrayView'
import EditMappingArray from '../components/EditMappingArray';
import EditDataTypeDialog from './EditDataTypeDialog';

import SelectTupleIndex from "@apricityhealth/web-common-lib/components/SelectTupleIndex";
import EditConditionArray from "../components/EditConditionArray";
import EditMedicationArray from "../components/EditMedicationArray";

const TEXT_OPTIONS = {
    "min": "",
    "max": "",
    "minScale": "",
    "multiline": "false",
    "minRows": 1
};

// enum value lists for control options that should be picked from a fixed set rather than typed freehand
const WEIGHT_UNITS = [
    { value: 'lbs', label: 'Pounds (lbs)' },
    { value: 'kg', label: 'Kilograms (kg)' }
];

const HEIGHT_UNITS = [
    { value: 'ft_in', label: 'Feet & inches' },
    { value: 'in', label: 'Inches (in)' },
    { value: 'cm', label: 'Centimeters (cm)' },
    { value: 'm', label: 'Meters (m)' }
];

const TEMPERATURE_UNITS = [
    { value: 'F', label: 'Fahrenheit (°F)' },
    { value: 'C', label: 'Celsius (°C)' }
];

// inputUnit may additionally be 'pref', meaning use the patient's selected temperature unit at runtime
const TEMPERATURE_INPUT_UNITS = [
    { value: 'pref', label: 'User Preference' },
    ...TEMPERATURE_UNITS
];

// must match FORMATS in web-common-lib BarcodeScannerDialog
const BARCODE_FORMATS = [
    'pdf_417', 'code_128', 'code_39', 'ean_13', 'ean_8',
    'upc_a', 'upc_e', 'itf', 'data_matrix', 'qr_code'
].map((v) => ({ value: v, label: v }));

const CONTROL_TYPES = {
    "text": {
        "name": "Text"
    },
    "edit": {
        "name": "Editable Text",
        "options": TEXT_OPTIONS
    },
    "number": {
        "name": "Number",
        "options": TEXT_OPTIONS
    },
    "spinner": {
        "name": "Spinner"
    },
    "singleton": {
        "name": "Singleton"
    },
    "image": {
        "name": "Image"
    },
    "bodyMap": {
        "name": "Body Map"
    },
    "uploadImage": {
        "name": "Upload Image",
        "options": {
            "maxImageCount": "1",
            "enableMultipleImages": "false"
        }
    },
    "date": {
        "name": "Date"
    },
    "datetime": {
        "name": "Date & Time"
    },
    "patientData": {
        "name": "Patient Data",
        "options": {
            "dataId": "",
            "tupleIndex": "0",
            "days": "14"
        }
    },
    "systolicBloodPressure": {
        "name": "Systolic BP",
        "options": TEXT_OPTIONS
    },
    "diastolicBloodPressure": {
        "name": "Diastolic BP",
        "options": TEXT_OPTIONS
    },
    "temperature": {
        "name": "Temperature",
        "options": { ...TEXT_OPTIONS, inputUnit: 'pref', targetUnit: 'F' },
        "enums": { inputUnit: { values: TEMPERATURE_INPUT_UNITS }, targetUnit: { values: TEMPERATURE_UNITS } }
    },
    "oxygen": {
        "name": "Oxygen",
        "options": TEXT_OPTIONS
    },
    "heartRate": {
        "name": "Heart Rate",
        "options": TEXT_OPTIONS
    },
    "steps": {
        "name": "Steps",
        "options": TEXT_OPTIONS
    },
    "weight": {
        "name": "Weight",
        "options": { ...TEXT_OPTIONS, inputUnit: 'lbs', targetUnit: 'kg' },
        "enums": { inputUnit: { values: WEIGHT_UNITS }, targetUnit: { values: WEIGHT_UNITS } }
    },
    "height": {
        "name": "Height",
        "options": { inputUnit: 'ft_in', targetUnit: 'cm' },
        "enums": { inputUnit: { values: HEIGHT_UNITS }, targetUnit: { values: HEIGHT_UNITS } }
    },
    "barcode": {
        "name": "Barcode",
        "options": {
            "formats": "pdf_417,qr_code"
        },
        "enums": { formats: { values: BARCODE_FORMATS, multiple: true } }
    },
    "slider": {
        "name": "Slider",
        "options": {
            "min": "1",
            "max": "10",
            "scale": "1",
            "tickStep": "",
            "minLabel": "",
            "maxLabel": ""
        }
    },
    "button": {
        "name": "Button",
        "options": {
            "action": "",
            "args": "",
            "title": "",
            "description": "",
            "subject": "",
            "message": ""
        }
    },
    "droplist": {
        "name": "Droplist",
        "options": {
            "options": "1:Yes,0:No",
            "width": "300"
        }
    }
};

class EditAnswerDialog extends Component {
    constructor(props) {
        super(props);

        this.state = {
            texts: props.texts,
            language: props.language || 'en-us',
            answer: null,
            question: props.question,
            progress: null,
            error: null,
            dialog: null,
            done: false
        };
    }

    componentDidMount() {
        this.loadAnswer();
    }

    loadAnswer() {
        const ANSWER_TEMPLATE = {
            answerId: uuidv4(),
            text: [''],
            explanation: '',
            controlType: "text",
            controlOptions: {},
            andFlagConditions: [],
            orFlagConditions: [],
            addFlags: [],
            removeFlags: [],
            mapping: [],
            bounds: [],
            addRequiredData: [],
            addOptionalData: [],
            addModels: [],
            addConditions: [],
            addMedications: [],
            preConditions: '',
            detect: false,
            bail: false,
            default: false,
            noCulling: false,
            break: false,
            calculate: false,
            calculateAnswerIds: [],
            calculateOp: 'sum'
        };

        const answer = { ...ANSWER_TEMPLATE, ...(JSON.parse(JSON.stringify(this.props.answer || {}))) };
        this.setState({ answer });
    }

    onCancel() {
        const { onCancel } = this.props;
        if (onCancel)
            onCancel();
    }

    onDone() {
        console.log("onDone:", this.state.answer);
        const { onDone } = this.props;
        if (onDone)
            onDone(this.state.answer);
    }

    onSelectDataType(type) {
        let { answer } = this.state;
        answer.dataId = type ? type.dataId : '';
        this.setState({ answer });
    }

    onSelectBaselineType(type) {
        let { answer } = this.state;
        answer.baselineId = type ? type.dataId : '';
        this.setState({ answer });
    }

    displayPreview() {
        const self = this;
        let { answer } = this.state;
        let text = answer.text[0];

        this.setState({
            dialog: <Dialog fullWidth={true} maxWidth="sm" modal="true" open={true}>
                <DialogTitle>Preview</DialogTitle>
                <DialogContent>
                    <div style={{ margin: 5 }}>
                        <div dangerouslySetInnerHTML={{ __html: text }}></div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" style={styles.button} onClick={() => {
                        self.setState({ dialog: null });
                    }}>OK</Button>
                </DialogActions>
            </Dialog>
        });
    }

    onAddQuestionIdsUsingModels() {
        console.log("onAddQuestionIdsUsingModels");
        const { answer, question } = this.state;
        const { DataTypesStore } = this.props.appContext.stores;
        let dataId = answer.dataId || question.dataId;
        if (dataId) {
            answer.questionIds = DataTypesStore.findRelatedQuestionIdsForDataId(dataId);
            this.setState({ answer });
        }
    }

    getAdvancedOptions() {
        const self = this;

        const { answer, question } = this.state;
        const { appContext } = this.props;

        const otherAnswers = (question && Array.isArray(question.answers))
            ? question.answers.filter((a) => a.answerId && a.answerId !== answer.answerId)
            : [];

        return <div>
            <SelectDataType
                appContext={appContext}
                label="Baseline Data Type"
                dataId={answer.baselineId}
                enableNone={true}
                onCreate={() => {
                    self.setState({
                        dialog: <EditDataTypeDialog appContext={this.props.appContext}
                            dataType={{ dataId: '', name: '', description: '', category: 'symptom', tupleDescriptions: [] }}
                            onCancel={() => self.setState({ dialog: null })}
                            onDone={(newType) => {
                                answer.baselineId = newType.dataId;
                                self.setState({ dialog: null, answer });
                            }} />
                    });
                }}
                onSelect={this.onSelectBaselineType.bind(this)} />
            <br />
            <SelectTupleIndex
                appContext={appContext}
                label="Baseline Tuple"
                dataId={answer.baselineId}
                tupleIndex={answer.baselineTuple}
                onChange={(tupleIndex) => answer.baselineTuple = tupleIndex} />
            <br />
            <SelectDataType
                appContext={appContext}
                label="Autofill Data Type"
                dataId={answer.autofillId}
                enableNone={true}
                onCreate={() => {
                    self.setState({
                        dialog: <EditDataTypeDialog appContext={this.props.appContext}
                            dataType={{ dataId: '', name: '', description: '', category: 'symptom', tupleDescriptions: [] }}
                            onCancel={() => self.setState({ dialog: null })}
                            onDone={(newType) => {
                                answer.autofillId = newType.dataId;
                                self.setState({ dialog: null, answer });
                            }} />
                    });
                }}
                onSelect={(type) => self.setState({ answer: { ...answer, autofillId: type ? type.dataId : '' } })} />
            <br />
            <SelectTupleIndex
                appContext={appContext}
                label="Autofill Tuple"
                dataId={answer.autofillId}
                tupleIndex={answer.autofillTuple}
                onChange={(tupleIndex) => answer.autofillTuple = tupleIndex} />
            <Tooltip title='How many days in the past can we find data to autofill. 0 to only look for new data in the future.'>
                <TextField type='number' style={{ width: 150, margin: 5 }} value={answer.autofillDays} label='Autofill Days'
                    onChange={(e) => this.setState({ answer: { ...answer, autofillDays: e.target.value } })} />
            </Tooltip>
            <Tooltip title='If checked, this answer will become read-only once it has been autofilled.'>
                <FormControlLabel style={{ marginLeft: '0px' }}
                    control={<Checkbox
                        checked={answer.autofillLock}
                        onChange={(e) => self.setState({ answer: { ...answer, autofillLock: e.target.checked } })}
                    />
                    }
                    label="Autofill Lock" />
            </Tooltip>
            <br />
            <Tooltip title='If checked, this answer will be calculated automatically from the values of other answers in this question.'>
                <FormControlLabel style={{ marginLeft: '0px' }}
                    control={<Checkbox
                        checked={!!answer.calculate}
                        onChange={(e) => self.setState({ answer: { ...answer, calculate: e.target.checked } })}
                    />
                    }
                    label="Calculate" />
            </Tooltip>
            {answer.calculate && <div>
                <FormControl style={{ width: 150, margin: 5 }}>
                    <InputLabel>Calculate Op</InputLabel>
                    <Select
                        value={answer.calculateOp || 'sum'}
                        onChange={(e) => {
                            answer.calculateOp = e.target.value;
                            self.setState({ answer });
                        }}>
                        <MenuItem value='sum'>Sum</MenuItem>
                        <MenuItem value='average'>Average</MenuItem>
                        <MenuItem value='mean'>Mean</MenuItem>
                        <MenuItem value='median'>Median</MenuItem>
                        <MenuItem value='min'>Min</MenuItem>
                        <MenuItem value='max'>Max</MenuItem>
                        <MenuItem value='mul'>Multiply</MenuItem>
                        <MenuItem value='div'>Divide</MenuItem>
                    </Select>
                </FormControl>
                <FormControl style={{ margin: 5, width: 450 }}>
                    <InputLabel shrink>Calculate From Answers</InputLabel>
                    <Select multiple
                        displayEmpty
                        value={Array.isArray(answer.calculateAnswerIds) ? answer.calculateAnswerIds : []}
                        onChange={(e) => {
                            answer.calculateAnswerIds = e.target.value;
                            self.setState({ answer });
                        }}
                        renderValue={(selected) => (selected || []).map((id) => {
                            const a = otherAnswers.find((x) => x.answerId === id);
                            return (a && Array.isArray(a.text) ? a.text[0] : '') || id;
                        }).join(', ')}>
                        {otherAnswers.length === 0 && <MenuItem disabled value=''>No other answers in this question</MenuItem>}
                        {otherAnswers.map((a) => {
                            const selected = Array.isArray(answer.calculateAnswerIds) && answer.calculateAnswerIds.indexOf(a.answerId) > -1;
                            const itemText = (Array.isArray(a.text) ? a.text[0] : '') || '';
                            return <MenuItem key={a.answerId} value={a.answerId}>
                                <Checkbox checked={selected} />
                                <ListItemText primary={itemText} />
                            </MenuItem>;
                        })}
                    </Select>
                </FormControl>
            </div>}
            <br />
            <EditFlagArrayView appContext={appContext} array={answer.andFlagConditions} label={'And Flags'}
                onChange={(flags) => answer.andFlagConditions = flags}
                tip={'Flags required to display this answer, all flags must be active.'} />
            <br />
            <EditFlagArrayView appContext={appContext} array={answer.orFlagConditions} label={'Or Flags'}
                onChange={(flags) => answer.orFlagConditions = flags}
                tip={'Flags required to display this answer, any of the flags may be active.'} />
            <br />
            <EditConditions
                appContext={appContext}
                conditions={answer.preConditions}
                onChange={(conditions) => {
                    answer.preConditions = conditions;
                    self.setState({ answer });
                }}
                name={'Pre Conditions'}
            />
            <EditModelTypeArrayView appContext={appContext} array={answer.addModels} label={'Add Models'}
                tip={'Add the given models to the conversation when this answer is given, all data needed by the given model will be requested.'} />
            <br />
            <EditDataTypeArray
                appContext={appContext}
                array={answer.addRequiredData}
                label={'Required Data'}
                onChange={(addRequiredData) => {
                    answer.addRequiredData = addRequiredData
                    self.setState({ answer });
                }}
                tip={'Any data types specified will be aquired when this answer is given.'}
            />
            <br />
            <EditFlagArrayView appContext={appContext} array={answer.addFlags} disableNot={true} label={'Add Flags'}
                onChange={(flags) => answer.addFlags = flags}
                tip={'Add conversation flags which can affect conversation flow.'} />
            <br />
            <EditFlagArrayView appContext={appContext} array={answer.removeFlags} disableNot={true} label={'Remove Flags'}
                onChange={(flags) => answer.removeFlags = flags}
                tip={'Remove conversation flags which can affect the conversation flow.'} />
            <br />
            <EditConditionArray appContext={appContext} array={answer.addConditions} label={'Add Conditions'}
                onChange={(cond) => answer.addConditions = cond}
                tip={'Add the given condition to the patient record when this answer is given.'} />
            <br />
            <EditMedicationArray appContext={appContext} array={answer.addMedications} label={'Add Medications'}
                onChange={(meds) => answer.addMedications = meds}
                tip={'Add the given medications to the patient record when this answer is given.'} />
            <br />
            <EditMappingArray appContext={appContext} array={answer.bounds} label={'Bounds'}
                onChange={(mapping) => answer.bounds = mapping}
                tip={'Allow answer values only between the given min and max.'} />
            <br />
            <EditMappingArray appContext={appContext} array={answer.mapping} label={'Mapping'}
                onChange={(mapping) => answer.mapping = mapping}
                tip={'Remap an answer value into a given score based on the answer being between a given min and max.'} />
            <br />
        </div>
    }

    onSelectControlType(e) {
        const { answer } = this.state;
        if (e.target.value !== answer.controlType) {
            answer.controlType = e.target.value;

            const options = CONTROL_TYPES[answer.controlType].options;
            if (options) {
                for (let j in answer.controlOptions) {
                    if (options[j] === undefined) delete answer.controlOptions[j];
                }
                for (let k in options) {
                    answer.controlOptions[k] = options[k];
                }
            }
            else {
                for (let j in answer.controlOptions) delete answer.controlOptions[j];
            }
            this.setState({ answer });
        }
    }

    // Editor for answer.controlOptions. Options declared with an enum (see CONTROL_TYPES) are
    // picked from a drop-down; a multiple enum (e.g. barcode formats) is a comma-joined list.
    // Everything else stays a free-form text field.
    renderControlOptions() {
        const self = this;
        const { answer } = this.state;
        if (!answer.controlOptions) answer.controlOptions = {};
        const controlOptions = answer.controlOptions;
        const type = CONTROL_TYPES[answer.controlType] || {};
        const enums = type.enums || {};

        // schema-defined options first (in declared order), then any extra keys already present
        const schemaKeys = type.options ? Object.keys(type.options) : [];
        const keys = [...schemaKeys, ...Object.keys(controlOptions).filter((k) => !schemaKeys.includes(k))];
        if (keys.length === 0) return null;

        return <div style={{ margin: 5 }}>
            <FormLabel>Control Options</FormLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {keys.map((key) => {
                    const enumSpec = enums[key];
                    if (enumSpec) {
                        const multiple = enumSpec.multiple === true;
                        const raw = controlOptions[key];
                        const value = multiple
                            ? String(raw || '').split(',').map((s) => s.trim()).filter(Boolean)
                            : (raw !== undefined ? raw : '');
                        return <FormControl key={key} style={{ minWidth: 200, margin: 5 }}>
                            <InputLabel>{key}</InputLabel>
                            <Select
                                multiple={multiple}
                                value={value}
                                renderValue={multiple ? (selected) => selected.join(', ') : undefined}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    controlOptions[key] = multiple ? (Array.isArray(v) ? v : [v]).join(',') : v;
                                    self.setState({ answer });
                                }}>
                                {enumSpec.values.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {multiple && <Checkbox checked={value.indexOf(opt.value) > -1} />}
                                        <ListItemText primary={opt.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>;
                    }
                    return <TextField key={key} label={key} style={{ minWidth: 200, margin: 5 }}
                        value={controlOptions[key] !== undefined ? controlOptions[key] : ''}
                        onChange={(e) => {
                            controlOptions[key] = e.target.value;
                            self.setState({ answer });
                        }} />;
                })}
            </div>
        </div>;
    }

    render() {
        const self = this;
        const { appContext } = this.props;
        const { answer, progress, dialog } = this.state;

        if (!answer) return null;
        if (answer.category === undefined) {
            answer.category = '';
        }
        if (answer.score === undefined) {
            answer.score = '0';
        }
        if (answer.noAnswerScore === undefined) {
            answer.noAnswerScore = '';
        }

        function getControlTypes() {
            const items = [];
            const types = Object.keys(CONTROL_TYPES).sort();
            types.forEach((k) => {
                items.push(<MenuItem key={k} value={k}>{CONTROL_TYPES[k].name}</MenuItem>);
            });
            return items;
        }

        return (
            <Dialog model="false" open={true} fullWidth={true} maxWidth={'sm'} >
                <DialogTitle>Edit Answer</DialogTitle>
                <DialogContent>
                    <TextField
                        style={styles.answer}
                        label="Category"
                        value={answer.category}
                        onChange={(e) => {
                            answer.category = e.target.value;
                            self.setState({ answer });
                        }}
                    />
                    <br />
                    <TextField
                        style={styles.answer}
                        label="Answer Text"
                        value={answer.text[0]}
                        onChange={(e) => {
                            answer.text[0] = e.target.value;
                            self.setState({ answer });
                        }}
                    />
                    <IconButton onClick={self.displayPreview.bind(self)}><InfoIcon /></IconButton>
                    <br />
                    <TextField
                        style={styles.answer}
                        label="Explanation Text"
                        value={answer.explanation}
                        onChange={(e) => {
                            answer.explanation = e.target.value;
                            self.setState({ answer });
                        }}
                    />
                    <br />
                    <TextField
                        style={styles.answer}
                        label="Answer Id:"
                        value={answer.answerId}
                        onChange={(e) => {
                            answer.answerId = e.target.value;
                            self.setState({ answer });
                        }}
                    />
                    <br />
                    <Tooltip title='If checked, then detect will be triggered by this answer'>
                        <FormControlLabel style={{ marginLeft: '0px' }}
                            control={<Checkbox
                                checked={answer.detect}
                                onChange={(e) => {
                                    answer.detect = e.target.checked;
                                    self.setState({ answer });
                                }}
                            />
                            }
                            label="Detect" />
                    </Tooltip>
                    <Tooltip title='If checked, then bail will be triggered when by this answer. All data collected will be deleted.'>
                        <FormControlLabel style={{ marginLeft: '0px' }}
                            control={<Checkbox
                                checked={answer.bail}
                                onChange={(e) => {
                                    answer.bail = e.target.checked;
                                    self.setState({ answer });
                                }}
                            />
                            }
                            label="Bail" />
                    </Tooltip>
                    <Tooltip title='If checked, then this answer will be selected by default.'>
                        <FormControlLabel style={{ marginLeft: '0px' }}
                            control={<Checkbox
                                checked={answer.default}
                                onChange={(e) => {
                                    answer.default = e.target.checked;
                                    self.setState({ answer });
                                }}
                            />
                            }
                            label="Default" />
                    </Tooltip>
                    <Tooltip title='If checked, then this answer will not be culled if no model is requesting the data provided by this answer.'>
                        <FormControlLabel style={{ marginLeft: '0px' }}
                            control={<Checkbox
                                checked={answer.noCulling}
                                onChange={(e) => {
                                    answer.noCulling = e.target.checked;
                                    self.setState({ answer });
                                }}
                            />
                            }
                            label="No Culling" />
                    </Tooltip>
                    <Tooltip title='If checked, a line break will occur before this answer forcing it to a new row.'>
                        <FormControlLabel style={{ marginLeft: '0px' }}
                            control={<Checkbox
                                checked={answer.break}
                                onChange={(e) => {
                                    answer.break = e.target.checked;
                                    self.setState({ answer });
                                }}
                            />
                            }
                            label="Break" />
                    </Tooltip>
                    <br />
                    <Tooltip title='If checked, this answer is optional even if the question is required or a checklist.'>
                        <FormControlLabel style={{ marginLeft: '0px' }}
                            control={<Checkbox
                                checked={answer.optional}
                                onChange={(e) => {
                                    answer.optional = e.target.checked;
                                    self.setState({ answer });
                                }}
                            />
                            }
                            label="Optional" />
                    </Tooltip>
                    <Tooltip title='If checked, this answer is disabled by default. Useful for autofill or calculated answers.'>
                        <FormControlLabel style={{ marginLeft: '0px' }}
                            control={<Checkbox
                                checked={answer.disabled}
                                onChange={(e) => {
                                    answer.disabled = e.target.checked;
                                    self.setState({ answer });
                                }}
                            />
                            }
                            label="Disabled" />
                    </Tooltip>
                    <br />
                    <FormControl style={styles.select}>
                        <InputLabel>Control Type</InputLabel>
                        <Select
                            value={answer.controlType}
                            onChange={this.onSelectControlType.bind(this)}
                            input={<Input name="controlType" id="controlType-helper" />}
                        >
                            {getControlTypes()}
                        </Select>
                    </FormControl>
                    <br />
                    {this.renderControlOptions()}
                    <br />
                    <SelectDataType
                        appContext={appContext}
                        dataId={answer.dataId}
                        enableNone={true}
                        onCreate={() => {
                            self.setState({
                                dialog: <EditDataTypeDialog appContext={this.props.appContext}
                                    dataType={{ dataId: '', name: '', description: '', category: 'symptom', tupleDescriptions: [] }}
                                    onCancel={() => self.setState({ dialog: null })}
                                    onDone={(newType) => {
                                        answer.dataId = newType.dataId;
                                        self.setState({ dialog: null, answer });
                                    }} />
                            });
                        }}
                        onSelect={this.onSelectDataType.bind(this)} />
                    <br />
                    <FormControl style={{ width: 150, margin: 5 }}>
                        <InputLabel>Score</InputLabel>
                        <Select
                            value={answer.score}
                            onChange={(e) => {
                                answer.score = e.target.value;
                                self.setState({ answer });
                            }}>
                            <MenuItem value='0'>0</MenuItem>
                            <MenuItem value='1'>1</MenuItem>
                            <MenuItem value='2'>2</MenuItem>
                            <MenuItem value='3'>3</MenuItem>
                            <MenuItem value='4'>4</MenuItem>
                            <MenuItem value='NaN'>NaN</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField style={{ width: 150, margin: 5 }} label='Custom Score' value={answer.score} onChange={(e) => {
                        answer.score = e.target.value;
                        self.setState({ answer })
                    }} />
                    <Tooltip title='If no answer was given, assign this score to this answer.'>
                        <FormControl style={{ width: 150, margin: 5 }}>
                            <InputLabel>NA Score</InputLabel>
                            <Select
                                value={answer.noAnswerScore}
                                onChange={(e) => {
                                    answer.noAnswerScore = e.target.value;
                                    self.setState({ answer });
                                }}>
                                <MenuItem value=''>None</MenuItem>
                                <MenuItem value='0'>0</MenuItem>
                                <MenuItem value='1'>1</MenuItem>
                                <MenuItem value='2'>2</MenuItem>
                                <MenuItem value='3'>3</MenuItem>
                                <MenuItem value='4'>4</MenuItem>
                                <MenuItem value='NaN'>NaN</MenuItem>
                            </Select>
                        </FormControl>
                    </Tooltip>
                    <br />
                    <br />
                    <Accordion style={{ backgroundColor: '#D0D0D0' }}>
                        <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
                            Advanced Options
                        </AccordionSummary>
                        <AccordionDetails>
                            {self.getAdvancedOptions()}
                        </AccordionDetails>
                    </Accordion>
                    {progress}
                    {dialog}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" style={styles.button} onClick={(e) => { self.onDone() }}>Save</Button>
                    <Button variant="contained" style={styles.button} onClick={(e) => { self.onCancel() }}>Cancel</Button>
                </DialogActions>
            </Dialog>
        )
    }
}

const styles = {
    answer: {
        margin: 5,
        width: 450
    },
    name: {
        margin: 5,
        width: 300
    },
    select: {
        margin: 5,
        width: 300
    },
    div: {
        margin: 10
    },

    button: {
        margin: 10
    }
};

export default EditAnswerDialog;
